import { signal, computed } from "@preact/signals-core"

// Flat store of plain objects
export const provisions = signal([])

// Shared empty Map, useful for when when `computed` compares by reference
const EMPTY_MAP = new Map()

let orderedProvisionSqids = []

// Cache from the previous run so we can preserve references
let lastBySection = new Map() // sectionSqid -> Map(fragmentId -> Set(types))

// Arrary to store provisions in initially so we don't churn on setup
let preInitProvisions = []
let isInitialized = false
let initScheduled = false
const initialize = () => {
  computeOrderedProvisionSqids()
  const orderedProvisions = orderedProvisionSqids
    .map(ps => preInitProvisions.find(p => p.provisionSqid === ps))
    .filter(Boolean)
  provisions.value = orderedProvisions
  preInitProvisions = null
  isInitialized = true
}

// Helper with modern Set APIs
const setsAreEquivalent = (a, b) => a === b || (!!a && a.isSubsetOf(b) && a.isSupersetOf(b))
const mapKeysAreEquivalent = (a, b) => {
  if (a === b) return true
  if (!a || a.size !== b.size) return false
  for (const k of b.keys()) if (!a.has(k)) return false
  return true
}

// Master computed: section -> (fragmentId -> Set(types))
export const sectionFragmentTypes = computed(() => {
  const src = provisions.value

  // Build mutable accumulator: section -> fragmentId -> Set(types)
  const temp = new Map()
  for (let i = 0; i < src.length; i++) {
    const p = src[i]
    let fragMap = temp.get(p.sectionSqid)
    if (!fragMap) {
      fragMap = new Map()
      temp.set(p.sectionSqid, fragMap)
    }
    for (let j = 0; j < p.fragmentIds.length; j++) {
      const fid = p.fragmentIds[j]
      let set = fragMap.get(fid)
      if (!set) {
        set = new Set()
        fragMap.set(fid, set)
      }
      set.add(p.type)
    }
  }

  // Build the next result, reusing references where contents are unchanged
  const nextBySection = new Map()

  for (const [sectionId, newFragMapMutable] of temp) {
    const prevFragMap = lastBySection.get(sectionId)

    let fragmentChanged = false
    const rebuiltFragMap = new Map()

    // Reuse Set references when equal
    for (const [fid, newSetMutable] of newFragMapMutable) {
      const newSet = new Set(newSetMutable) // create stable output Set
      const prevSet = prevFragMap?.get(fid)

      if (setsAreEquivalent(prevSet, newSet)) {
        rebuiltFragMap.set(fid, prevSet) // preserve reference
      } else {
        rebuiltFragMap.set(fid, newSet)
        fragmentChanged = true
      }
    }

    // Detect removed fragmentIds
    if (prevFragMap) {
      for (const prevFid of prevFragMap.keys()) {
        if (!rebuiltFragMap.has(prevFid)) {
          fragmentChanged = true
          break
        }
      }
    }

    // If nothing changed and key sets match, reuse the whole Map reference
    if (!fragmentChanged && mapKeysAreEquivalent(prevFragMap, rebuiltFragMap)) {
      nextBySection.set(sectionId, prevFragMap)
    } else {
      nextBySection.set(sectionId, rebuiltFragMap)
    }
  }

  // Sections that disappeared are omitted callers fall back to EMPTY_MAP.

  lastBySection = nextBySection
  return nextBySection
})

// Per-section computed (memoized)
const sectionComputedCache = new Map() // sectionSqid -> computed

export function getFragmentsForSection(sectionSqid) {
  let c = sectionComputedCache.get(sectionSqid)
  if (!c) {
    c = computed(() => sectionFragmentTypes.value.get(sectionSqid) ?? EMPTY_MAP)
    sectionComputedCache.set(sectionSqid, c)
  }
  return c
}

export function add(provision) {
  if(isInitialized) {
    computeOrderedProvisionSqids()
    const orderedProvisions = orderedProvisionSqids
      .map(ps => provisions.value.find(p => p.provisionSqid === ps)
        || (provision.provisionSqid == ps ? provision : null))
      .filter(Boolean)
    provisions.value = orderedProvisions
  } else {
    preInitProvisions.push(provision)
    if (!initScheduled) {
      initScheduled = true
      queueMicrotask(initialize)
    }
  }
}

export function remove(provisionSqid) {
  provisions.value = provisions.value.filter(p => p.provisionSqid !== provisionSqid)
}

export function edit(updated) {
  provisions.value = provisions.value.map(p =>
    p.provisionSqid === updated.provisionSqid ? { ...p, ...updated } : p
  )
}

const computeOrderedProvisionSqids = () => {
  const container = document.getElementById('provisions-accordion')
  const elements = Array.from(container.querySelectorAll('.accordion-item'));
  orderedProvisionSqids = elements.map(el => el.dataset.provisionSqid)
};